import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Button,
  Card,
  CardContent,
  Select,
  Checkbox,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { ROLE_DEFINITIONS, getUserRole } from '../common/permissionUtils';
import { hasPermission } from '../common/permissionUtils';

const buildSelectValues = (values) => (Array.isArray(values) ? values : []);

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    sendNotification,
    editNotifications,
    editSettings,
    fetchUsersOnce,
  } = api;
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const notificationdata = useSelector(state => state.notificationdata);
  const usersdata = useSelector(state => state.usersdata);
  const dispatch = useDispatch();

  const canSend = hasPermission(auth.profile, settings, 'notifications', 'update') ||
    hasPermission(auth.profile, settings, 'notifications', 'create');
  const canEdit = hasPermission(auth.profile, settings, 'notifications', 'update');
  const canDelete = hasPermission(auth.profile, settings, 'notifications', 'delete');

  useEffect(() => {
    if (!usersdata.staticusers) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, usersdata.staticusers]);

  const [tabValue, setTabValue] = useState(0);
  const [history, setHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({
    title: '',
    body: '',
    usertype: 'All',
    devicetype: 'All',
    targetRoles: [],
    targetPartners: [],
    targetFleets: [],
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState('');

  const templates = settings?.notificationTemplates || [];
  const segments = settings?.notificationSegments || [];

  useEffect(() => {
    if (notificationdata.notifications) {
      const sorted = [...notificationdata.notifications].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setHistory(sorted);
    } else {
      setHistory([]);
    }
  }, [notificationdata.notifications]);

  const partnerOptions = useMemo(() => {
    const partners = settings?.partnerPages || [];
    const list = Array.isArray(partners) ? partners : Object.values(partners || {});
    return list
      .map((partner) => ({
        value: partner.slug || partner.partnerSlug || partner.companySlug || '',
        label: partner.name || partner.companyName || partner.slug || '',
      }))
      .filter((item) => item.value);
  }, [settings?.partnerPages]);

  const fleetOptions = useMemo(() => {
    const list = usersdata.staticusers || [];
    return list
      .filter((user) => user.usertype === 'fleetadmin')
      .map((user) => ({
        value: user.id,
        label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id,
      }))
      .filter((item) => item.value);
  }, [usersdata.staticusers]);

  const roleOptions = useMemo(() => ROLE_DEFINITIONS.map((role) => ({ value: role.key, label: role.label })), []);

  const filteredHistory = useMemo(() => {
    if (!searchQuery) return history;
    const query = searchQuery.toLowerCase();
    return history.filter(d =>
      (d.title && d.title.toLowerCase().includes(query)) ||
      (d.body && d.body.toLowerCase().includes(query))
    );
  }, [history, searchQuery]);

  const setFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    setSelectedTemplateId(templateId);
    setForm({
      title: template.title || '',
      body: template.body || '',
      usertype: template.usertype || 'All',
      devicetype: template.devicetype || 'All',
      targetRoles: template.targetRoles || [],
      targetPartners: template.targetPartners || [],
      targetFleets: template.targetFleets || [],
    });
  };

  const applySegment = (segmentId) => {
    const segment = segments.find((item) => item.id === segmentId);
    if (!segment) return;
    setSelectedSegmentId(segmentId);
    setForm((prev) => ({
      ...prev,
      usertype: segment.usertype || 'All',
      devicetype: segment.devicetype || prev.devicetype,
      targetRoles: segment.targetRoles || [],
      targetPartners: segment.targetPartners || [],
      targetFleets: segment.targetFleets || [],
    }));
  };

  const handleSend = () => {
    if (!canSend) return;
    if (!form.title || !form.body) {
      alert(t('title_body_required') || 'Title and message are required');
      return;
    }
    const payload = {
      ...form,
      createdAt: Date.now(),
      createdBy: auth.profile?.uid || null,
      createdByRole: getUserRole(auth.profile),
    };
    dispatch(sendNotification(payload));
    dispatch(editNotifications(payload, 'Add'));
  };

  const updateSettingsList = (field, list) => {
    if (!settings) return;
    dispatch(editSettings({
      ...settings,
      [field]: list,
    }));
  };

  const historyColumns = [
    { title: t('title'), field: 'title' },
    { title: t('body'), field: 'body' },
    { title: t('user_type'), field: 'usertype' },
    { title: t('device_type'), field: 'devicetype' },
    { title: t('created_at') || 'Created', field: 'createdAt', render: (row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : '-' },
  ];

  return (
    notificationdata.loading ? <CircularLoading/> : (
      <Box>
        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)} sx={{ mb: 2 }}>
          <Tab label={t('notification_campaigns') || 'Campaigns'} />
          <Tab label={t('notification_templates') || 'Templates'} />
          <Tab label={t('notification_segments') || 'Segments'} />
        </Tabs>

        {tabValue === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={5}>
              <Card sx={{ borderRadius: 3, border: '1px solid #E5E7EB' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('send_notification') || 'Send notification'}
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label={t('title') || 'Title'}
                      value={form.title}
                      onChange={(e) => setFormField('title', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label={t('body') || 'Message'}
                      value={form.body}
                      onChange={(e) => setFormField('body', e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Select
                          fullWidth
                          value={form.usertype}
                          onChange={(e) => setFormField('usertype', e.target.value)}
                        >
                          <MenuItem value="All">{t('all') || 'All'}</MenuItem>
                          <MenuItem value="customer">{t('customer') || 'Customer'}</MenuItem>
                          <MenuItem value="driver">{t('driver') || 'Driver'}</MenuItem>
                        </Select>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Select
                          fullWidth
                          value={form.devicetype}
                          onChange={(e) => setFormField('devicetype', e.target.value)}
                        >
                          <MenuItem value="All">{t('all') || 'All'}</MenuItem>
                          <MenuItem value="ANDROID">{t('android') || 'Android'}</MenuItem>
                          <MenuItem value="IOS">{t('ios') || 'iOS'}</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    <Select
                      fullWidth
                      value={selectedTemplateId}
                      onChange={(e) => applyTemplate(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">{t('apply_template') || 'Apply template'}</MenuItem>
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                      ))}
                    </Select>

                    <Select
                      fullWidth
                      value={selectedSegmentId}
                      onChange={(e) => applySegment(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">{t('apply_segment') || 'Apply segment'}</MenuItem>
                      {segments.map((segment) => (
                        <MenuItem key={segment.id} value={segment.id}>{segment.name}</MenuItem>
                      ))}
                    </Select>

                    <Select
                      fullWidth
                      multiple
                      value={buildSelectValues(form.targetRoles)}
                      onChange={(e) => setFormField('targetRoles', e.target.value)}
                      renderValue={(selected) => selected.map((item) => roleOptions.find((role) => role.value === item)?.label || item).join(', ')}
                    >
                      {roleOptions.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          <Checkbox checked={buildSelectValues(form.targetRoles).includes(role.value)} />
                          <Typography>{role.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>

                    <Select
                      fullWidth
                      multiple
                      value={buildSelectValues(form.targetPartners)}
                      onChange={(e) => setFormField('targetPartners', e.target.value)}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {partnerOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={buildSelectValues(form.targetPartners).includes(option.value)} />
                          <Typography>{option.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>

                    <Select
                      fullWidth
                      multiple
                      value={buildSelectValues(form.targetFleets)}
                      onChange={(e) => setFormField('targetFleets', e.target.value)}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {fleetOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={buildSelectValues(form.targetFleets).includes(option.value)} />
                          <Typography>{option.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>

                    <Button
                      variant="contained"
                      startIcon={<SendRoundedIcon />}
                      onClick={handleSend}
                      disabled={!canSend}
                    >
                      {t('send') || 'Send'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  p: 2,
                  mb: 2,
                  bgcolor: '#FFFFFF',
                  borderRadius: 3,
                  border: '1px solid #E5E7EB',
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  placeholder={t('search') || 'Search notifications...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    minWidth: 220,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 50,
                      bgcolor: '#F7F7F7',
                      '& fieldset': { border: 'none' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Chip
                  label={`${filteredHistory.length} ${t('push_notification_title') || 'notifications'}`}
                  sx={{ ml: 'auto', bgcolor: '#F3F4F6' }}
                />
              </Box>

              <MaterialTable
                icons={TableIcons}
                title={null}
                columns={historyColumns}
                style={{
                  direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
                  borderRadius: 12,
                  boxShadow: 'none',
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                }}
                data={filteredHistory}
                options={{
                  ...tableOptions,
                  search: false,
                  toolbar: false,
                  showTitle: false,
                }}
                editable={canDelete ? {
                  onRowDelete: oldData =>
                    new Promise(resolve => {
                      setTimeout(() => {
                        resolve();
                        dispatch(editNotifications(oldData, "Delete"));
                      }, 600);
                    }),
                } : {}}
              />
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <MaterialTable
            icons={TableIcons}
            title={null}
            columns={[
              { title: t('name') || 'Name', field: 'name' },
              { title: t('title') || 'Title', field: 'title' },
              { title: t('body') || 'Message', field: 'body' },
            ]}
            data={templates}
            options={{ ...tableOptions, search: true, toolbar: true }}
            editable={canEdit ? {
              onRowAdd: newData => new Promise((resolve) => {
                const next = [...templates, { ...newData, id: Date.now().toString() }];
                updateSettingsList('notificationTemplates', next);
                resolve();
              }),
              onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                const next = templates.map((item) => item.id === oldData.id ? { ...oldData, ...newData } : item);
                updateSettingsList('notificationTemplates', next);
                resolve();
              }),
              onRowDelete: oldData => new Promise((resolve) => {
                const next = templates.filter((item) => item.id !== oldData.id);
                updateSettingsList('notificationTemplates', next);
                resolve();
              }),
            } : {}}
          />
        )}

        {tabValue === 2 && (
          <MaterialTable
            icons={TableIcons}
            title={null}
            columns={[
              { title: t('name') || 'Name', field: 'name' },
              { title: t('user_type') || 'User type', field: 'usertype' },
              {
                title: t('roles') || 'Roles',
                field: 'targetRoles',
                render: (row) => (row.targetRoles || []).join(', ') || '-',
                editComponent: (props) => (
                  <Select
                    multiple
                    fullWidth
                    value={props.value || []}
                    onChange={(event) => props.onChange(event.target.value)}
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        <Checkbox checked={(props.value || []).includes(role.value)} />
                        <Typography>{role.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                ),
              },
              {
                title: t('allowed_partners') || 'Partners',
                field: 'targetPartners',
                render: (row) => (row.targetPartners || []).join(', ') || '-',
                editComponent: (props) => (
                  <Select
                    multiple
                    fullWidth
                    value={props.value || []}
                    onChange={(event) => props.onChange(event.target.value)}
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    {partnerOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={(props.value || []).includes(option.value)} />
                        <Typography>{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                ),
              },
              {
                title: t('allowed_fleets') || 'Fleets',
                field: 'targetFleets',
                render: (row) => (row.targetFleets || []).join(', ') || '-',
                editComponent: (props) => (
                  <Select
                    multiple
                    fullWidth
                    value={props.value || []}
                    onChange={(event) => props.onChange(event.target.value)}
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    {fleetOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={(props.value || []).includes(option.value)} />
                        <Typography>{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                ),
              },
            ]}
            data={segments}
            options={{ ...tableOptions, search: true, toolbar: true }}
            editable={canEdit ? {
              onRowAdd: newData => new Promise((resolve) => {
                const next = [...segments, { ...newData, id: Date.now().toString() }];
                updateSettingsList('notificationSegments', next);
                resolve();
              }),
              onRowUpdate: (newData, oldData) => new Promise((resolve) => {
                const next = segments.map((item) => item.id === oldData.id ? { ...oldData, ...newData } : item);
                updateSettingsList('notificationSegments', next);
                resolve();
              }),
              onRowDelete: oldData => new Promise((resolve) => {
                const next = segments.filter((item) => item.id !== oldData.id);
                updateSettingsList('notificationSegments', next);
                resolve();
              }),
            } : {}}
          />
        )}
      </Box>
    )
  );
}
